/**
 * Shared "start an offline check" helper. Single entry point used by
 * every offline barcode-resolution path in the UI:
 *
 *   - Live camera scan (CreateLayer.tsx → handleScan)
 *   - Typed/pasted label + Save click (CreateLayer.tsx → handleSave)
 *   - Autocomplete pick (useLayerModal.ts → setLayerSelect when offline)
 *
 * Before this helper existed, the camera-scan path had its own copy of
 * the session-creation logic (`goToScannedLayerOffline`) and the other
 * two paths either bypassed offline session creation entirely or threw
 * "Saving…" toasts that never resolved. The duplication made it easy
 * to leave entry points behind whenever the offline contract changed.
 *
 * The helper does NOT navigate. Callers do that themselves with the
 * route they prefer — that keeps the helper React-router agnostic and
 * lets `setLayerSelect` reuse its existing `urlAppendix` computation.
 *
 * Returns either `{ ok: true, sessionId }` on success or
 * `{ ok: false, reason }` describing the user-facing toast to show.
 * Callers display the reason and stop; we don't throw because the
 * surfaces calling this are React event handlers.
 */

import { ROOT_URL } from "../constants/urls";
import { getCachedETagForUrl } from "./etagInterceptor";
import { lookupByBarcode } from "./layerCache";
import { createSession, setActiveSessionId } from "./sessionManager";
import type { CachedLayer } from "./types";

interface StartOfflineCheckParams {
    /** Raw barcode value the user scanned, typed, or picked. */
    label: string;
    /**
     * Optional cached layer object. When the caller already has the
     * layer (autocomplete pick: react-select returned the cached row),
     * pass it here to skip the IDB roundtrip. When called from a raw
     * barcode (camera scan / typed label), leave undefined and the
     * helper will resolve via lookupByBarcode.
     */
    layer?: { id?: string | null; label?: string; type?: string | null; fruit_type?: string | null } | null;
    /**
     * check_location.value, e.g. "intake". Sessions need it. The
     * helper validates and returns a user-facing reason if missing —
     * accept `undefined` here so call sites can pass `useState`-shaped
     * values without an `as string` cast.
     */
    checkType: string | undefined;
    /** Tenant ID for the configs/active ETag lookup that becomes config_hash. */
    tenantId: string | undefined;
}

export type StartOfflineCheckResult =
    | { ok: true; sessionId: string; layerId: string }
    | { ok: false; reason: string };

export async function startOfflineCheck(
    params: StartOfflineCheckParams,
): Promise<StartOfflineCheckResult> {
    const { label, layer, checkType, tenantId } = params;

    if (!checkType) {
        return { ok: false, reason: "Please select a check type before scanning offline." };
    }
    if (!tenantId) {
        return { ok: false, reason: "Could not determine tenant; please reload the page." };
    }

    // Resolve the cached layer. Prefer the caller-provided object (for
    // autocomplete picks where react-select hands us the row directly);
    // otherwise look it up by label.
    let cached: CachedLayer | null = null;
    if (layer && layer.id && layer.label && layer.type && layer.fruit_type) {
        cached = layer as CachedLayer;
    } else {
        cached = await lookupByBarcode(label);
    }
    if (!cached) {
        return { ok: false, reason: "This pallet is not in the local cache. Please scan when connected." };
    }

    // Capture the most recent /configs/active ETag as config_hash. The
    // replay engine compares this against the live ETag at sync time
    // and fails the session on a mismatch (config drift detection).
    // No cached ETag means this device has never been online for this
    // tenant — refuse rather than create a session that will fail at
    // sync time.
    const configHash = await getCachedETagForUrl(
        `${ROOT_URL}api/tenants/${tenantId}/configs/active`,
    );
    if (!configHash) {
        return { ok: false, reason: "Please open the app online at least once before going offline." };
    }

    const session = await createSession(label, cached, {
        idempotency_key:
            typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
                ? crypto.randomUUID()
                : `key_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`,
        location: checkType,
        is_manual: true,
        config_hash: configHash,
        meta: [],
    });
    setActiveSessionId(session.id);
    return { ok: true, sessionId: session.id, layerId: cached.id };
}
